import React from 'react';
import { Link } from 'react-router-dom';
import { getRecentNews, CATEGORY_LABELS, CATEGORY_TONES } from '../data/news.js';

// ─────────────────────────────────────────────────────────────────────────
// THE WIRE — homepage industry-news section (s69)
// ─────────────────────────────────────────────────────────────────────────
//
// Drops in below the article-rows section, above the newsletter, on the
// homepage. Six cards from src/data/news.js, sorted newest-first.
//
// Design contract:
//   - Uses ONLY the existing theme tokens passed in via the `T` prop.
//     No new colors, no new fonts. The look should read as a natural
//     extension of the article-rows section above it: same kicker
//     style, same Fraunces typography, same sienna accent.
//   - Pulsing live-dot in the section header (CSS keyframe — no JS).
//   - Each card's "READ" target is the matching internal blog post.
//   - NO outbound links anywhere in this section. Source attribution
//     is text only. Outbound credit lives at the bottom of the blog
//     post itself (where it belongs editorially and where the link
//     equity reflects properly to the source).
//   - ARCHIVE link in the footer goes to the /news hub.
//
// Rotation workflow lives in src/data/news.js — see the header comment
// there. This component is render-only; it shouldn't need to change
// when items rotate.
//
// Mobile: the 2-col card grid collapses to 1-col below 820px via the
// matching .wire-grid media query, which Homepage.jsx already declares
// alongside the other grid breakpoints.

export default function WireSection({ T, fontDisplay, fontBody, fontMono }) {
  const items = getRecentNews(6);

  // Format ISO "YYYY-MM-DD" to "MAY 13" — JetBrains Mono kicker style,
  // matching how the BlogIndex date column is rendered.
  const formatShort = (iso) => {
    if (!iso) return '';
    const d = new Date(iso + 'T12:00:00');
    return d
      .toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      .toUpperCase();
  };

  // Resolve a category tone name ("sienna" / "sage" / "inkSoft") against
  // the theme. Falls back to sienna if a category is missing from the
  // tones map — safer than crashing on a typo.
  const toneFor = (cat) => {
    const toneName = CATEGORY_TONES[cat] || 'sienna';
    return T[toneName] || T.sienna;
  };

  return (
    <section
      className="reveal reveal-3 wire-section"
      aria-labelledby="wire-heading"
      style={{ marginBottom: 100 }}
    >
      {/*
        Scoped keyframe for the pulsing live-dot. The section's own
        <style> block keeps this animation local — Homepage already
        defines fadeUp / reveal and a global @keyframes wirePulse would
        risk colliding with future additions.
      */}
      <style>{`
        @keyframes wwgWirePulse {
          0%, 100% { opacity: 0.35; transform: scale(0.82); }
          50%      { opacity: 1;    transform: scale(1); }
        }
        .wire-pulse {
          display: inline-block;
          width: 8px; height: 8px;
          background: ${T.sienna};
          border-radius: 50%;
          margin-right: 10px;
          vertical-align: middle;
          animation: wwgWirePulse 1.4s ease-in-out infinite;
        }
        .wire-card { transition: background 0.2s, border-color 0.2s; cursor: pointer; }
        .wire-card:hover { background: ${T.surface}; border-color: ${T.sienna}; }
        .wire-card:hover .wire-arrow { transform: translateX(4px); color: ${T.sienna}; }
        .wire-arrow { display: inline-block; transition: transform 0.2s, color 0.2s; }

        @media (max-width: 820px) {
          .wire-grid { grid-template-columns: 1fr !important; }
          .wire-header-row {
            flex-direction: column !important;
            align-items: flex-start !important;
            gap: 8px;
          }
        }
      `}</style>

      {/* Section header — live indicator + ARCHIVE link */}
      <div
        className="wire-header-row"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          marginBottom: 16,
        }}
      >
        <div
          style={{
            fontFamily: fontMono,
            fontSize: 11,
            letterSpacing: '0.18em',
            color: T.sienna,
          }}
        >
          <span className="wire-pulse" aria-hidden="true" />
          <span>§ THE WIRE &nbsp;·&nbsp; INDUSTRY NEWS DESK</span>
        </div>
        <Link
          to="/news"
          className="ghost-link"
          style={{
            fontFamily: fontMono,
            fontSize: 11,
            letterSpacing: '0.18em',
            color: T.inkFaint,
            textDecoration: 'none',
          }}
        >
          ARCHIVE →
        </Link>
      </div>

      {/* Headline */}
      <h2
        id="wire-heading"
        className="hero-h1"
        style={{
          fontFamily: fontDisplay,
          fontSize: 56,
          fontWeight: 400,
          lineHeight: 1.06,
          letterSpacing: '-0.025em',
          margin: '0 0 12px',
          maxWidth: 800,
        }}
      >
        What the trade is reading.{' '}
        <em
          style={{
            fontStyle: 'italic',
            color: T.inkSoft,
            fontWeight: 400,
          }}
        >
          A weekly read
        </em>{' '}
        of what's shipping, what's shifting, and what we think it means.
      </h2>

      {/* Sub-rail */}
      <p
        style={{
          fontFamily: fontDisplay,
          fontSize: 18,
          fontWeight: 300,
          fontStyle: 'italic',
          lineHeight: 1.5,
          color: T.inkSoft,
          margin: '0 0 40px',
          maxWidth: 720,
        }}
      >
        Filed by the Window Washing Guide News Desk from trade press, glass-industry releases, and policy filings. No aggregation. Our own read of each story.
      </p>

      {/* Card grid — 2 columns desktop, 1 column mobile (via media query) */}
      <div
        className="wire-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 0,
          borderTop: `1px solid ${T.rule}`,
        }}
      >
        {items.map((item, i) => {
          const isRightCol = i % 2 === 1;
          const lastRowIdx = Math.floor((items.length - 1) / 2);
          const myRowIdx = Math.floor(i / 2);
          const isLastRow = myRowIdx === lastRowIdx;
          const tone = toneFor(item.category);

          return (
            <Link
              key={item.slug}
              to={`/blog/${item.slug}`}
              className="wire-card"
              aria-label={`${item.headline} — read the full piece`}
              style={{
                display: 'flex',
                flexDirection: 'column',
                textDecoration: 'none',
                color: T.ink,
                padding: '24px 24px 22px',
                paddingLeft: isRightCol ? 24 : 0,
                paddingRight: !isRightCol ? 24 : 0,
                borderRight: i % 2 === 0 ? `1px solid ${T.rule}` : 'none',
                borderBottom: isLastRow ? 'none' : `1px solid ${T.rule}`,
                minHeight: 200,
              }}
            >
              {/* Date + category row */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  marginBottom: 14,
                  fontFamily: fontMono,
                  fontSize: 10,
                  letterSpacing: '0.18em',
                }}
              >
                <span style={{ color: T.inkFaint }}>
                  {formatShort(item.publishedDate)}
                </span>
                <span style={{ color: tone }}>
                  {CATEGORY_LABELS[item.category] || 'INDUSTRY'}
                </span>
              </div>

              {/* Headline */}
              <h3
                style={{
                  fontFamily: fontDisplay,
                  fontSize: 21,
                  fontWeight: 400,
                  lineHeight: 1.2,
                  letterSpacing: '-0.015em',
                  margin: '0 0 12px',
                  color: T.ink,
                }}
              >
                {item.headline}
              </h3>

              {/* Summary */}
              <p
                style={{
                  fontFamily: fontBody,
                  fontSize: 14,
                  lineHeight: 1.55,
                  color: T.inkSoft,
                  margin: '0 0 18px',
                  flex: 1,
                }}
              >
                {item.summary}
              </p>

              {/* Attribution + read arrow */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  paddingTop: 14,
                  borderTop: `1px solid ${T.rule}`,
                  fontFamily: fontMono,
                  fontSize: 10,
                  letterSpacing: '0.15em',
                }}
              >
                {/*
                  Source name as plain text — NOT a link. The Wire never
                  links out from the homepage; the outbound credit is on
                  the blog post itself.
                */}
                <span style={{ color: T.inkFaint }}>
                  VIA {(item.source && item.source.name
                    ? item.source.name
                    : 'WIRE').toUpperCase()}
                </span>
                <span
                  className="wire-arrow"
                  style={{
                    color: T.sienna,
                    letterSpacing: '0.1em',
                  }}
                >
                  READ →
                </span>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
